import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ticketsApi, type VerifyTicketResponse } from "@/lib/api";
import { toast } from "sonner";
import { QrCode, ScanLine, CheckCircle2, XCircle, Loader2, Camera, RotateCcw } from "lucide-react";

export const Route = createFileRoute("/admin/scan-tickets")({
  head: () => ({
    meta: [{ title: "Scan Tickets — OyoBooking" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: ScanTicketsPage,
});

const SCANNER_ELEMENT_ID = "qr-scan-region";

function ScanTicketsPage() {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState<VerifyTicketResponse | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  async function startScanner() {
    setCameraError(null);
    setResult(null);
    if (scannerRef.current) {
      await stopScanner();
    }
    const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID);
    scannerRef.current = scanner;
    setScanning(true);
    try {
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decodedText) => {
          await scanner.pause();
          setScanning(false);
          await verifyTicket(decodedText);
        },
        () => {}
      );
    } catch (err) {
      setScanning(false);
      const msg = err instanceof Error ? err.message : "Could not start camera";
      setCameraError(msg);
      toast.error("Camera error: " + msg);
    }
  }

  async function stopScanner() {
    try {
      if (scannerRef.current?.isScanning) {
        await scannerRef.current.stop();
      }
      await scannerRef.current?.clear();
    } catch {
      // ignore
    } finally {
      scannerRef.current = null;
      setScanning(false);
    }
  }

  async function verifyTicket(code: string) {
    setVerifying(true);
    setResult(null);
    try {
      const res = await ticketsApi.verify(code.trim());
      setResult(res);
      if (res.valid) {
        toast.success(res.message);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Verification failed";
      toast.error(msg);
      setResult({ valid: false, message: msg, ticketRef: null, ticketCode: null, tierName: null, tierType: null, attendeeName: null, eventName: null, eventDate: null, venueName: null, isCheckedIn: null, checkedInAt: null });
    } finally {
      setVerifying(false);
    }
  }

  function handleReset() {
    setResult(null);
    setCameraError(null);
    startScanner();
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display text-2xl font-bold flex items-center gap-2">
          <ScanLine className="size-6 text-primary" /> Ticket Scanner
        </h1>
        <p className="text-muted-foreground">Scan a guest's QR code to verify and check them in.</p>
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="bg-muted/30">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Camera className="size-4" /> Camera
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div id={SCANNER_ELEMENT_ID} className="mx-auto max-w-sm aspect-square rounded-xl overflow-hidden border bg-black" />
          {!scanning && !result && !verifying && !cameraError && (
            <div className="mt-4 flex justify-center">
              <Button onClick={startScanner} className="rounded-full">
                <QrCode className="size-4 mr-2" /> Start Scanner
              </Button>
            </div>
          )}
          {scanning && (
            <div className="mt-4 flex justify-center">
              <Button variant="outline" onClick={stopScanner} className="rounded-full">
                Stop Scanner
              </Button>
            </div>
          )}
          {cameraError && (
            <div className="mt-4 text-center text-sm text-red-600">
              <p className="mb-2">{cameraError}</p>
              <Button variant="outline" onClick={startScanner} className="rounded-full">
                <RotateCcw className="size-4 mr-2" /> Retry
              </Button>
            </div>
          )}
          {verifying && (
            <div className="mt-6 flex flex-col items-center gap-2 text-muted-foreground">
              <Loader2 className="size-6 animate-spin" />
              <p className="text-sm">Verifying ticket...</p>
            </div>
          )}
        </CardContent>
      </Card>

      {result && (
        <Card className={`mt-6 border-l-4 ${result.valid ? "border-l-green-500" : "border-l-red-500"}`}>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              {result.valid ? <CheckCircle2 className="size-8 text-green-500" /> : <XCircle className="size-8 text-red-500" />}
              <div className="flex-1 space-y-2">
                <h3 className={`font-semibold ${result.valid ? "text-green-700" : "text-red-700"}`}>
                  {result.valid ? "Ticket Verified" : "Verification Failed"}
                </h3>
                <p className="text-muted-foreground">{result.message}</p>
                {result.ticketRef && (
                  <div className="grid gap-1 text-sm pt-2">
                    <p><span className="text-muted-foreground">Ticket:</span> {result.ticketRef}</p>
                    {result.tierName && <p><span className="text-muted-foreground">Tier:</span> {result.tierName}</p>}
                    {result.attendeeName && <p><span className="text-muted-foreground">Attendee:</span> {result.attendeeName}</p>}
                    {result.eventName && <p><span className="text-muted-foreground">Event:</span> {result.eventName}</p>}
                    {result.eventDate && (
                      <p><span className="text-muted-foreground">Date:</span> {new Date(result.eventDate).toLocaleString()}</p>
                    )}
                    {result.isCheckedIn && <p className="text-slate-600">Already checked in</p>}
                  </div>
                )}
                <div className="pt-4">
                  <Button onClick={handleReset} className="rounded-full">
                    <RotateCcw className="size-4 mr-2" /> Scan Next
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
